/**
 * ★ 2026-10-22 — Playlist video Verification Pass (hub IT/ES/EN, sotto la lista territori).
 * Player principale + miniature per cambiare video (lazy: l'iframe si carica solo per il
 * video selezionato; al cambio parte in autoplay). Con 1 solo video = player semplice.
 * Evento reale `video_play` (consenso analytics) quando l'utente avvia/cambia video.
 */
import React, { useEffect, useState } from 'react';
import { Play } from 'lucide-react';
import { youtubeThumb } from '../../lib/testimonials';
import { trackFunnelEvent } from '../../lib/attribution';

const DEFAULT_TOKENS = { ink: '#081712', emerald: '#0C2B21', gold: '#D6B25E', ivory: '#F4EEE1', sand: '#CFC6B2' };

export function VpVideoPlaylist({ videos, lang = 'it', territory = 'AL', funnel = 'it', tokens: tk, testIdPrefix = 'ital-vp-video', className = '' }) {
  const tokens = { ...DEFAULT_TOKENS, ...(tk || {}) };
  const list = Array.isArray(videos) ? videos : [];
  const [active, setActive] = useState(0);
  const [autoplay, setAutoplay] = useState(false);

  // Cambio territorio → riparti dal primo video, senza autoplay
  useEffect(() => { setActive(0); setAutoplay(false); }, [territory]);

  if (list.length === 0) return null;
  const cur = list[Math.min(active, list.length - 1)];
  const tt = (v) => (v.title && (v.title[lang] || v.title.it)) || '';
  const mm = (v) => (v.meta && (v.meta[lang] || v.meta.it)) || '';

  const pick = (i) => {
    if (i === active) return;
    setActive(i);
    setAutoplay(true);
    trackFunnelEvent('video_play', { funnel, label: 'vp_video', video_id: list[i].id, territory, position: i + 1 });
  };

  const labelNow = { it: 'In riproduzione', es: 'Reproduciendo', en: 'Now playing' }[lang] || 'In riproduzione';
  const labelMore = { it: 'Altri video del Verification Pass', es: 'Más vídeos del Verification Pass', en: 'More Verification Pass videos' }[lang] || 'Altri video';

  return (
    <div className={`rounded-2xl overflow-hidden border ${className}`} style={{ borderColor: `${tokens.gold}66`, boxShadow: '0 20px 60px rgba(0,0,0,0.45)', background: tokens.ink }} data-testid={`${testIdPrefix}`} data-video-id={cur.id} data-count={list.length}>
      <div className="aspect-video">
        <iframe
          key={`${territory}-${cur.id}-${autoplay ? 'a' : 'p'}`}
          src={`https://www.youtube.com/embed/${cur.id}?rel=0&modestbranding=1${autoplay ? '&autoplay=1' : ''}`}
          title={tt(cur)}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          loading="lazy"
          data-testid={`${testIdPrefix}-iframe`}
        />
      </div>
      {list.length > 1 && (
        <div className="p-3 sm:p-4" data-testid={`${testIdPrefix}-list`}>
          <div className="flex items-center justify-between gap-2 text-[10px] font-sans font-bold tracking-[0.22em] uppercase" style={{ color: tokens.sand }}>
            <span className="truncate"><span style={{ color: tokens.gold }}>{labelNow}:</span> {tt(cur)}</span>
            <span className="shrink-0">{active + 1}/{list.length}</span>
          </div>
          <div className="mt-2.5 grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(list.length, 3)}, minmax(0, 1fr))` }} role="list" aria-label={labelMore}>
            {list.map((v, i) => {
              const isActive = i === active;
              return (
                <button
                  key={v.id}
                  type="button"
                  role="listitem"
                  onClick={() => pick(i)}
                  aria-current={isActive ? 'true' : undefined}
                  aria-label={tt(v)}
                  className="group relative text-left rounded-lg overflow-hidden border transition-transform duration-150 hover:scale-[1.02] focus-visible:outline focus-visible:outline-2"
                  style={{ borderColor: isActive ? tokens.gold : `${tokens.gold}33`, boxShadow: isActive ? `0 0 0 1px ${tokens.gold}` : 'none', outlineColor: tokens.gold }}
                  data-testid={`${testIdPrefix}-thumb-${i + 1}`}
                  data-active={isActive ? 'true' : 'false'}
                >
                  <span className="block aspect-video relative" style={{ background: tokens.emerald }}>
                    <img src={youtubeThumb(v.id, 'mqdefault')} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ opacity: isActive ? 0.55 : 0.9 }} loading="lazy" />
                    <span className="absolute inset-0 grid place-items-center">
                      <span className="w-8 h-8 rounded-full grid place-items-center" style={{ background: isActive ? tokens.gold : 'rgba(244,238,225,0.92)' }}>
                        <Play className="w-3.5 h-3.5 ml-0.5" style={{ color: tokens.ink }} fill={tokens.ink} aria-hidden="true" />
                      </span>
                    </span>
                  </span>
                  <span className="block px-2 py-1.5" style={{ background: `${tokens.emerald}F0` }}>
                    <span className="block text-[11px] font-sans font-semibold leading-snug line-clamp-2" style={{ color: tokens.ivory }}>{tt(v)}</span>
                    <span className="block text-[10px] font-sans mt-0.5 truncate" style={{ color: tokens.sand }}>{mm(v)}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
